import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  School,
  Person,
  Assignment,
  Assessment,
  TrendingUp,
  EmojiEvents,
  CalendarToday,
  Notifications,
  CheckCircle,
  Schedule,
  Class,
  MenuBook,
  Edit,
  Visibility,
  Payment,
  Close as CloseIcon,
  Settings,
  Add,
  Delete,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Admin Dashboard
  const AdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [changingPassword, setChangingPassword] = useState(false);

    const BRAND = {
      a: '#5c55f3',
      b: '#7c75ff',
      gradient: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
      gradientSoft: 'linear-gradient(135deg, rgba(92,85,243,0.12) 0%, rgba(124,117,255,0.10) 100%)',
      surface: 'rgba(255, 255, 255, 0.92)',
      stroke: 'rgba(255,255,255,0.18)',
      ink: '#0f172a',
      muted: '#64748b',
    };

    useEffect(() => {
      fetchAdminStats();
    }, []);

    const fetchAdminStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/dashboard/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDashboardData(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    const handleOpenPasswordDialog = () => {
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
      setPasswordErrors({});
      setOpenPasswordDialog(true);
    };

    const handleClosePasswordDialog = () => {
      setOpenPasswordDialog(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
      setPasswordErrors({});
    };

    const handlePasswordChange = (field, value) => {
      setPasswordData({ ...passwordData, [field]: value });
      if (passwordErrors[field]) {
        setPasswordErrors({ ...passwordErrors, [field]: null });
      }
    };

    const handleChangePassword = async () => {
      const errors = {};
      
      if (!passwordData.current_password) {
        errors.current_password = 'Current password is required';
      }
      if (!passwordData.new_password) {
        errors.new_password = 'New password is required';
      } else if (passwordData.new_password.length < 8) {
        errors.new_password = 'Password must be at least 8 characters';
      }
      if (passwordData.new_password !== passwordData.new_password_confirmation) {
        errors.new_password_confirmation = 'Passwords do not match';
      }

      setPasswordErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }

      try {
        setChangingPassword(true);
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/profile/change-password`,
          passwordData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Password changed successfully!');
        handleClosePasswordDialog();
      } catch (error) {
        if (error.response?.data?.errors) {
          setPasswordErrors(error.response.data.errors);
        } else if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Failed to change password. Please try again.');
        }
      } finally {
        setChangingPassword(false);
      }
    };

    const getChangeChipSx = (changeText) => {
      const value = String(changeText ?? '').trim();
      const hasPlus = value.includes('+');
      const hasMinus = value.includes('-');

      if (hasPlus) {
        return {
          bgcolor: '#dcfce7',
          color: '#16a34a',
          fontWeight: 700,
          border: 'none',
        };
      }

      if (hasMinus) {
        return {
          bgcolor: '#fee2e2',
          color: '#dc2626',
          fontWeight: 700,
          border: 'none',
        };
      }

      return {
        bgcolor: '#e2e8f0',
        color: '#334155',
        fontWeight: 700,
        border: 'none',
      };
    };

    const stats = [
      { 
        title: 'Total Students', 
        count: dashboardData?.total_students || 0, 
        icon: <School />, 
        color: BRAND.a, 
        change: dashboardData?.monthly_stats?.students?.change || '0%' 
      },
      { 
        title: 'Total Teachers', 
        count: dashboardData?.total_teachers || 0, 
        icon: <Person />, 
        color: BRAND.b, 
        change: '0%' 
      },
      { 
        title: 'Active Exams', 
        count: dashboardData?.active_exams || 0, 
        icon: <Assignment />, 
        color: BRAND.a, 
        change: dashboardData?.monthly_stats?.exams?.change || '0%' 
      },
      { 
        title: 'Results Published', 
        count: dashboardData?.results_published || 0, 
        icon: <Assessment />, 
        color: BRAND.b, 
        change: '+0%' 
      },
    ];

    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: BRAND.gradient,
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
      }}>
        {/* Modern Header Card */}
        <Box sx={{ 
          mb: 4, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 }, 
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${BRAND.stroke}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h3" fontWeight="800" sx={{ 
                background: BRAND.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}>
                Welcome back, {user?.name}
              </Typography>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                Admin dashboard — manage your institution efficiently
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(BRAND.muted, 0.9), mt: 1, display: { xs: 'none', sm: 'block' } }}>
                Overview of students, teachers, exams and results
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenPasswordDialog}
                sx={{
                  borderColor: alpha(BRAND.a, 0.3),
                  color: BRAND.a,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: BRAND.a,
                    bgcolor: alpha(BRAND.a, 0.08),
                  }
                }}
              >
                Change Password
              </Button>
              <Avatar sx={{ 
                width: { xs: 56, sm: 70, md: 80 }, 
                height: { xs: 56, sm: 70, md: 80 }, 
                background: BRAND.gradient,
                fontSize: { xs: 28, sm: 32, md: 36 },
                fontWeight: 'bold',
                boxShadow: '0 8px 24px rgba(92, 85, 243, 0.4)'
              }}>
                {user?.name?.charAt(0)}
              </Avatar>
            </Box>
          </Box>
        </Box>

        {loading && (
          <Paper
            sx={{
              mb: 4,
              p: 4,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: `1px solid ${BRAND.stroke}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={22} sx={{ color: BRAND.a }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND.ink }}>
                  Loading dashboard data…
                </Typography>
                <LinearProgress
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha('#ffffff', 0.45),
                    '& .MuiLinearProgress-bar': { bgcolor: alpha(BRAND.a, 0.65) },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* Modern Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                transition: 'all 0.3s ease',
                border: `1px solid ${BRAND.stroke}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: alpha(stat.color, 0.14),
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ color: stat.color, fontSize: 32 }}>{stat.icon}</Box>
                    </Box>
                    <Chip 
                      label={stat.change} 
                      size="small"
                      sx={getChangeChipSx(stat.change)}
                    />
                  </Box>
                  <Typography variant="h3" fontWeight="900" sx={{ mb: 0.5, color: BRAND.ink, letterSpacing: -0.5 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 700 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Actions Section */}
        <Paper sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, md: 4 },
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: { xs: 2, md: 3 },
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: `1px solid ${BRAND.stroke}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
            <Box sx={{
              width: 4,
              height: { xs: 24, md: 32 },
              background: BRAND.gradient,
              borderRadius: 2,
              mr: 2
            }} />
            <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
              Quick Actions
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {[
              { icon: <School />, label: 'Manage Students', color: BRAND.a, route: '/admin/students' },
              { icon: <Person />, label: 'Manage Teachers', color: BRAND.b, route: '/admin/teachers' },
              { icon: <MenuBook />, label: 'Manage Subjects', color: BRAND.b, route: '/admin/subjects' },
              { icon: <Assignment />, label: 'Manage Exams', color: BRAND.a, route: '/admin/exams' },
              { icon: <Assessment />, label: 'Enter Results', color: BRAND.b, route: '/teacher/results-entry' },
              { icon: <TrendingUp />, label: 'Results Summary', color: '#10b981', route: '/teacher/results-summary' },
              { icon: <Payment />, label: 'Fund Collectors', color: '#f59e0b', route: '/admin/fund-collectors' },
            ].map((action, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                <Card 
                  onClick={() => navigate(action.route)}
                  sx={{ 
                    cursor: 'pointer',
                    background: alpha(action.color, 0.08),
                    border: `1px solid ${alpha(action.color, 0.25)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 14px 26px ${alpha(action.color, 0.35)}`,
                      background: alpha(action.color, 0.14),
                      borderColor: alpha(action.color, 0.55),
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 2.5, md: 3 }, px: { xs: 1, sm: 2 } }}>
                    <Box sx={{ 
                      color: action.color, 
                      fontSize: { xs: 36, sm: 42, md: 48 },
                      mb: { xs: 1, md: 1.5 },
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      {action.icon}
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#1e293b', fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                      {action.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Recent Activity & System Status */}
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                  Recent Activity
                </Typography>
              </Box>
              {dashboardData?.recent_students && dashboardData.recent_students.length > 0 ? (
                <Box>
                  {dashboardData.recent_students.map((student, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Avatar sx={{ 
                            background: BRAND.gradient,
                        mr: 2,
                        width: 48,
                        height: 48
                      }}>
                        {student.name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#1e293b' }}>
                          {student.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {student.student_index} • Registered on {student.created_at} 
                        </Typography>
                      </Box>
                      <Chip 
                        label="New" 
                        size="small" 
                        sx={{ 
                          bgcolor: '#dcfce7',
                          color: '#16a34a',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <School sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    No recent activity
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    New student registrations will appear here
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                  System Status
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#dcfce7',
                  borderRadius: 2,
                  mb: 2
                }}>
                  <CheckCircle sx={{ color: '#16a34a', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#166534' }}>
                      All Systems Operational
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#16a34a' }}>
                      Everything running smoothly
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#dbeafe',
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Schedule sx={{ color: '#2563eb', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#1e40af' }}>
                      {dashboardData?.pending_results || 0} Pending Results
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2563eb' }}>
                      Awaiting entry
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#f3e8ff',
                  borderRadius: 2
                }}>
                  <Notifications sx={{ color: '#9333ea', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#6b21a8' }}>
                      {dashboardData?.total_grades || 6} Grade Levels
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9333ea' }}>
                      Grades 6-11
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Recent Exams Card */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                  Recent Exams
                </Typography>
              </Box>
              {dashboardData?.recent_exams && dashboardData.recent_exams.length > 0 ? (
                <Box>
                  {dashboardData.recent_exams.slice(0, 3).map((exam, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#1e293b', mb: 0.5 }}>
                        {exam.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {exam.class} • {exam.exam_date}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Assignment sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    No recent exams
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Change Password Dialog */}
        <Dialog 
          open={openPasswordDialog} 
          onClose={handleClosePasswordDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ 
            background: BRAND.gradient,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings />
            Change Password
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
              Update your admin account password. Make sure it's at least 8 characters long.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  error={!!passwordErrors.current_password}
                  helperText={passwordErrors.current_password}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                  error={!!passwordErrors.new_password}
                  helperText={passwordErrors.new_password || "Must be at least 8 characters"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.new_password_confirmation}
                  onChange={(e) => handlePasswordChange('new_password_confirmation', e.target.value)}
                  error={!!passwordErrors.new_password_confirmation}
                  helperText={passwordErrors.new_password_confirmation}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={handleClosePasswordDialog}
              sx={{ color: BRAND.muted }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              disabled={changingPassword}
              onClick={handleChangePassword}
              sx={{
                background: BRAND.gradient,
                color: '#fff',
                '&:hover': {
                  background: BRAND.gradient,
                  opacity: 0.9,
                }
              }}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Teacher Dashboard
  const TeacherDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
    const [teacherSubjects, setTeacherSubjects] = useState([]);
    const [savingSubjects, setSavingSubjects] = useState(false);
    
    // Exam management states
    const [exams, setExams] = useState([]);
    const [loadingExams, setLoadingExams] = useState(false);
    const [openExamDialog, setOpenExamDialog] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);
    const [savingExam, setSavingExam] = useState(false);
    const [selectedExamGrade, setSelectedExamGrade] = useState('');
    const [examFormData, setExamFormData] = useState({
      name: '',
      type: 'midterm',
      grade: '',
      exam_date: '',
      description: '',
      status: 'draft'
    });
    const [examSubjects, setExamSubjects] = useState([
      { subject_name: 'Mathematics', total_marks: 100, passing_marks: 40 }
    ]);

    const grades = [6, 7, 8, 9, 10, 11];
    const [availableSubjects, setAvailableSubjects] = useState(['Mathematics', 'Science']);
    const examTypes = [
      { value: 'midterm', label: 'Mid-Term Exam' },
      { value: 'final', label: 'Final Exam' },
      { value: 'quiz', label: 'Quiz' },
      { value: 'assignment', label: 'Assignment' },
      { value: 'other', label: 'Other' }
    ];
    const examStatuses = [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'completed', label: 'Completed' }
    ];

    useEffect(() => {
      fetchTeacherStats();
    }, []);

    useEffect(() => {
      fetchTeacherExams();
    }, [selectedExamGrade]);

    useEffect(() => {
      if (selectedGrade || selectedSubject) {
        fetchStudents();
      }
    }, [selectedGrade, selectedSubject]);

    const fetchTeacherStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/dashboard/teacher`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDashboardData(response.data);
        // Set initial teacher subjects
        if (response.data.teacher_info?.subjects) {
          setTeacherSubjects(response.data.teacher_info.subjects);
          setAvailableSubjects(response.data.teacher_info.subjects);
        }
      } catch (error) {
      }
    };

    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const token = localStorage.getItem('token');
        const params = {};
        if (selectedGrade) params.grade = selectedGrade;
        if (selectedSubject) params.subject = selectedSubject;

        const response = await axios.get(`${API_URL}/teacher/students`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudents(response.data.students || []);
      } catch (error) {
        setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    };

    const handleSubjectToggle = (subject) => {
      setTeacherSubjects(prev => {
        if (prev.includes(subject)) {
          return prev.filter(s => s !== subject);
        } else {
          return [...prev, subject];
        }
      });
    };

    const handleSaveSubjects = async () => {
      if (teacherSubjects.length === 0) {
        alert('Please select at least one subject');
        return;
      }

      setSavingSubjects(true);
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/teacher/subjects`, {
          subjects: teacherSubjects
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setOpenSubjectDialog(false);
        fetchTeacherStats(); // Refresh dashboard
        fetchTeacherExams(); // Refresh exams
        alert('Subjects updated successfully!');
      } catch (error) {
        alert('Error updating subjects');
      } finally {
        setSavingSubjects(false);
      }
    };

    // Exam Management Functions
    const fetchTeacherExams = async () => {
      setLoadingExams(true);
      try {
        const token = localStorage.getItem('token');
        const params = {};
        if (selectedExamGrade) {
          params.grade = selectedExamGrade;
        }
        
        const response = await axios.get(`${API_URL}/teacher/exams`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        setExams(response.data || []);
      } catch (error) {
        setExams([]);
      } finally {
        setLoadingExams(false);
      }
    };

    const handleOpenExamDialog = (exam = null) => {
      if (exam) {
        setSelectedExam(exam);
        setExamFormData({
          name: exam.name,
          type: exam.type,
          grade: exam.grade,
          exam_date: exam.exam_date,
          description: exam.description || '',
          status: exam.status || 'draft'
        });
        setExamSubjects(exam.exam_subjects || [{ subject_name: 'Mathematics', total_marks: 100, passing_marks: 40 }]);
      } else {
        setSelectedExam(null);
        setExamFormData({
          name: '',
          type: 'midterm',
          grade: '',
          exam_date: '',
          description: '',
          status: 'draft'
        });
        setExamSubjects([{ subject_name: 'Mathematics', total_marks: 100, passing_marks: 40 }]);
      }
      setOpenExamDialog(true);
    };

    const handleCloseExamDialog = () => {
      setOpenExamDialog(false);
      setSelectedExam(null);
    };

    const handleExamFormChange = (field, value) => {
      setExamFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddExamSubject = () => {
      setExamSubjects(prev => [...prev, { subject_name: 'Mathematics', total_marks: 100, passing_marks: 40 }]);
    };

    const handleRemoveExamSubject = (index) => {
      if (examSubjects.length > 1) {
        setExamSubjects(prev => prev.filter((_, i) => i !== index));
      }
    };

    const handleExamSubjectChange = (index, field, value) => {
      setExamSubjects(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    };

    const handleSaveExam = async () => {
      // Validation
      if (!examFormData.name || !examFormData.grade || !examFormData.exam_date) {
        alert('Please fill in all required fields');
        return;
      }

      if (examSubjects.length === 0) {
        alert('Please add at least one subject');
        return;
      }

      setSavingExam(true);
      try {
        const token = localStorage.getItem('token');
        const data = {
          ...examFormData,
          subjects: examSubjects
        };

        if (selectedExam) {
          await axios.put(`${API_URL}/teacher/exams/${selectedExam.id}`, data, {
            headers: { Authorization: `Bearer ${token}` }
          });
          alert('Exam updated successfully!');
        } else {
          await axios.post(`${API_URL}/teacher/exams`, data, {
            headers: { Authorization: `Bearer ${token}` }
          });
          alert('Exam created successfully!');
        }

        handleCloseExamDialog();
        fetchTeacherExams();
      } catch (error) {
        alert('Error saving exam: ' + (error.response?.data?.message || error.message));
      } finally {
        setSavingExam(false);
      }
    };

    const handleDeleteExam = async (examId, examName) => {
      if (!window.confirm(`Are you sure you want to delete "${examName}"? This action cannot be undone.`)) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/teacher/exams/${examId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Exam deleted successfully!');
        fetchTeacherExams();
      } catch (error) {
        alert('Error deleting exam: ' + (error.response?.data?.message || error.message));
      }
    };

    const BRAND = {
      a: '#5c55f3',
      b: '#7c75ff',
      gradient: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
      gradientSoft: 'linear-gradient(135deg, rgba(92,85,243,0.12) 0%, rgba(124,117,255,0.10) 100%)',
      surface: 'rgba(255, 255, 255, 0.95)',
      stroke: 'rgba(255,255,255,0.18)',
      ink: '#0f172a',
      muted: '#64748b',
    };

    const stats = [
      { title: 'My Classes', count: '0', icon: <Class />, color: BRAND.a, change: '0%' },
      { title: 'My Subjects', count: dashboardData?.my_subjects || '0', icon: <MenuBook />, color: BRAND.b, change: '0%' },
      { title: 'Pending Marks', count: dashboardData?.pending_results || '0', icon: <Edit />, color: '#f59e0b', change: '0%' },
      { title: 'My Students', count: students.length || '0', icon: <School />, color: '#8b5cf6', change: '0%' },
    ];

    const quickActions = [
      { icon: <Assignment />, label: 'Manage Exams', color: '#667eea', route: '/teacher/exams', desc: 'Create and manage exams' },
      { icon: <Edit />, label: 'Enter Marks', color: BRAND.a, route: '/teacher/results-entry', desc: 'Add student exam results' },
      { icon: <Assessment />, label: 'Results Summary', color: '#10b981', route: '/teacher/results-summary', desc: 'View detailed analysis' },
    ];

    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: BRAND.gradient,
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
      }}>
        {/* Modern Header Card */}
        <Box sx={{ 
          mb: { xs: 3, md: 4 }, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 2.5, sm: 3, md: 4 }, 
          borderRadius: { xs: 3, md: 4 },
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${BRAND.stroke}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h3" fontWeight="800" sx={{ 
                background: BRAND.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}>
                Welcome back, {user?.name}
              </Typography>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                Teacher Portal — Manage your classes and assessments
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(BRAND.muted, 0.9), mt: 1, display: { xs: 'none', sm: 'block' } }}>
                Track student performance and enter examination results
              </Typography>
            </Box>
            <Avatar sx={{ 
              width: { xs: 56, sm: 70, md: 80 }, 
              height: { xs: 56, sm: 70, md: 80 }, 
              background: BRAND.gradient,
              fontSize: { xs: 28, sm: 32, md: 36 },
              fontWeight: 'bold',
              boxShadow: '0 8px 24px rgba(92, 85, 243, 0.4)'
            }}>
              {user?.name?.charAt(0)}
            </Avatar>
          </Box>
        </Box>

        {/* Modern Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                transition: 'all 0.3s ease',
                border: `1px solid ${BRAND.stroke}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: alpha(stat.color, 0.14),
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ color: stat.color, fontSize: 32 }}>{stat.icon}</Box>
                    </Box>
                    <Chip 
                      label={stat.change} 
                      size="small"
                      sx={{
                        bgcolor: '#e2e8f0',
                        color: '#334155',
                        fontWeight: 700,
                        border: 'none',
                      }}
                    />
                  </Box>
                  <Typography variant="h3" fontWeight="900" sx={{ mb: 0.5, color: BRAND.ink, letterSpacing: -0.5 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 700 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Teacher Info & My Subjects */}
          <Grid item xs={12} md={8}>
            {dashboardData?.teacher_info && (
              <Paper sx={{ 
                p: { xs: 2.5, sm: 3, md: 4 },
                mb: { xs: 2, sm: 2.5, md: 3 },
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                border: `1px solid ${BRAND.stroke}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 4,
                      height: 32,
                      background: BRAND.gradient,
                      borderRadius: 2,
                      mr: 2
                    }} />
                    <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                      My Subjects
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => setOpenSubjectDialog(true)}
                    sx={{
                      borderColor: alpha(BRAND.a, 0.3),
                      color: BRAND.a,
                      '&:hover': {
                        borderColor: BRAND.a,
                        bgcolor: alpha(BRAND.a, 0.08),
                      }
                    }}
                  >
                    Edit Subjects
                  </Button>
                </Box>
                {dashboardData.teacher_info.subjects?.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    {dashboardData.teacher_info.subjects.map((subject, index) => (
                      <Chip
                        key={index}
                        label={subject}
                        icon={subject === 'Mathematics' ? <MenuBook /> : <MenuBook />}
                        sx={{
                          bgcolor: alpha(BRAND.a, 0.15),
                          color: BRAND.a,
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          py: 2.5,
                          px: 1
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#fef3c7', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>
                      No subjects assigned yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#d97706' }}>
                      Click "Edit Subjects" to select your subjects
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* My Exams Section */}
            <Paper sx={{ 
              p: { xs: 2.5, sm: 3, md: 4 },
              mb: { xs: 2, sm: 2.5, md: 3 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: { xs: 2, md: 3 },
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 4,
                    height: 32,
                    background: BRAND.gradient,
                    borderRadius: 2,
                    mr: 2
                  }} />
                  <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                    My Exams
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleOpenExamDialog()}
                  sx={{
                    background: BRAND.gradient,
                    '&:hover': {
                      background: BRAND.gradient,
                      opacity: 0.9,
                    }
                  }}
                >
                  Add Exam
                </Button>
              </Box>

              {/* Grade Filter */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  label="Filter by Grade"
                  value={selectedExamGrade || ''}
                  onChange={(e) => setSelectedExamGrade(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All Grades</MenuItem>
                  {[6, 7, 8, 9, 10, 11].map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </TextField>
                {selectedExamGrade && (
                  <Chip
                    label={`Grade ${selectedExamGrade}`}
                    onDelete={() => setSelectedExamGrade('')}
                    sx={{
                      bgcolor: alpha(BRAND.a, 0.1),
                      color: BRAND.a,
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>

              {loadingExams ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={40} sx={{ color: BRAND.a }} />
                </Box>
              ) : exams.length > 0 ? (
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {exams.map((exam, index) => (
                    <Box
                      key={exam.id}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="700" sx={{ color: BRAND.ink, mb: 0.5 }}>
                            {exam.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                              label={`Grade ${exam.grade}`}
                              size="small"
                              sx={{
                                bgcolor: alpha(BRAND.a, 0.1),
                                color: BRAND.a,
                                fontWeight: 600
                              }}
                            />
                            <Chip
                              label={exam.type.charAt(0).toUpperCase() + exam.type.slice(1)}
                              size="small"
                              sx={{
                                bgcolor: alpha(BRAND.b, 0.1),
                                color: BRAND.b,
                                fontWeight: 600
                              }}
                            />
                            <Chip
                              label={exam.status === 'active' ? 'Active' : exam.status === 'completed' ? 'Completed' : 'Draft'}
                              size="small"
                              sx={{
                                bgcolor: exam.status === 'active' ? '#dcfce7' : exam.status === 'completed' ? '#e0e7ff' : '#fef3c7',
                                color: exam.status === 'active' ? '#16a34a' : exam.status === 'completed' ? '#4338ca' : '#d97706',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1 }}>
                            📅 {new Date(exam.exam_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </Typography>
                          {exam.description && (
                            <Typography variant="caption" sx={{ color: BRAND.muted, display: 'block', mb: 1 }}>
                              {exam.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {exam.exam_subjects?.map((subject, idx) => (
                              <Chip
                                key={idx}
                                label={`${subject.subject_name} (${subject.total_marks} marks)`}
                                size="small"
                                icon={<MenuBook />}
                                sx={{
                                  bgcolor: 'white',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '0.75rem'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenExamDialog(exam)}
                            sx={{
                              bgcolor: alpha(BRAND.a, 0.1),
                              '&:hover': {
                                bgcolor: alpha(BRAND.a, 0.2),
                              }
                            }}
                          >
                            <Edit sx={{ fontSize: 18, color: BRAND.a }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExam(exam.id, exam.name)}
                            sx={{
                              bgcolor: alpha('#ef4444', 0.1),
                              '&:hover': {
                                bgcolor: alpha('#ef4444', 0.2),
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Assignment sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500, mb: 1 }}>
                    No exams created yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
                    Create your first exam to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenExamDialog()}
                    sx={{
                      background: BRAND.gradient,
                      '&:hover': {
                        background: BRAND.gradient,
                        opacity: 0.9,
                      }
                    }}
                  >
                    Create Exam
                  </Button>
                </Box>
              )}
            </Paper>

            {/* My Students Section */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              mb: 3,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  My Students
                </Typography>
              </Box>

              {/* Filters */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    label="Filter by Grade"
                    size="small"
                  >
                    <MenuItem value="">All Grades</MenuItem>
                    {grades.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    label="Filter by Subject"
                    size="small"
                  >
                    <MenuItem value="">All Subjects</MenuItem>
                    {dashboardData?.teacher_info?.subjects?.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Student List */}
              {loadingStudents ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={40} sx={{ color: BRAND.a }} />
                </Box>
              ) : students.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: BRAND.muted }}>
                    Showing {students.length} student{students.length !== 1 ? 's' : ''}
                    {selectedGrade && ` in Grade ${selectedGrade}`}
                    {selectedSubject && ` for ${selectedSubject}`}
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {students.map((student, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                          mb: 1.5,
                          bgcolor: '#f8fafc',
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                          }
                        }}
                      >
                        <Avatar sx={{ 
                          background: BRAND.gradient,
                          mr: 2,
                          width: 40,
                          height: 40
                        }}>
                          {student.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ color: BRAND.ink }}>
                            {student.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: BRAND.muted }}>
                            {student.student_id} • {student.email}
                          </Typography>
                        </Box>
                        {student.subjects && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(() => {
                              try {
                                const subjects = typeof student.subjects === 'string' 
                                  ? JSON.parse(student.subjects) 
                                  : student.subjects;
                                return Array.isArray(subjects) ? subjects : [];
                              } catch (e) {
                                return [];
                              }
                            })().map((subj, idx) => (
                              <Chip
                                key={idx}
                                label={subj}
                                size="small"
                                sx={{
                                  bgcolor: alpha(BRAND.b, 0.1),
                                  color: BRAND.b,
                                  fontSize: '0.7rem'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <School sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    {selectedGrade || selectedSubject ? 'No students found' : 'Select grade or subject to view students'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    {selectedGrade || selectedSubject ? 'Try different filters' : 'Use the filters above to find your students'}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Today's Schedule */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Today's Schedule
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CalendarToday sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  No classes scheduled for today
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                  Your class schedule will appear here
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Quick Actions */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              mb: 3,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Quick Actions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(action.route)}
                    startIcon={action.icon}
                    sx={{
                      py: 1.5,
                      justifyContent: 'flex-start',
                      borderColor: alpha(action.color, 0.3),
                      color: action.color,
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: action.color,
                        bgcolor: alpha(action.color, 0.08),
                      }
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            </Paper>

            {/* Performance Summary */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              mb: 3,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  This Week
                </Typography>
              </Box>
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#dcfce7',
                  borderRadius: 2,
                  mb: 2
                }}>
                  <CheckCircle sx={{ color: '#16a34a', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#166534' }}>
                      0 Results Entered
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#16a34a' }}>
                      All up to date
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#dbeafe',
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Schedule sx={{ color: '#2563eb', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#1e40af' }}>
                      0 Classes Today
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2563eb' }}>
                      Check your schedule
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: '#fef3c7',
                  borderRadius: 2
                }}>
                  <Assignment sx={{ color: '#d97706', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#92400e' }}>
                      0 Pending Marks
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#d97706' }}>
                      No action needed
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Recent Activity */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: `1px solid ${BRAND.stroke}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Recent Exams
                </Typography>
              </Box>
              {dashboardData?.recent_exams && dashboardData.recent_exams.length > 0 ? (
                <Box>
                  {dashboardData.recent_exams.map((exam, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: BRAND.ink, mb: 0.5 }}>
                        {exam.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: BRAND.muted }}>
                        {exam.grade} • {exam.exam_date}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Assignment sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    No recent exams
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 0.5, display: 'block' }}>
                    Upcoming exams will appear here
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Subject Selection Dialog */}
        <Dialog open={openSubjectDialog} onClose={() => setOpenSubjectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="700">
                Select Your Subjects
              </Typography>
              <IconButton onClick={() => setOpenSubjectDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
              Choose the subjects you teach. You can select multiple subjects.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {availableSubjects.map((subject) => (
                <Paper
                  key={subject}
                  onClick={() => handleSubjectToggle(subject)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: teacherSubjects.includes(subject) ? `2px solid ${BRAND.a}` : '2px solid #e2e8f0',
                    bgcolor: teacherSubjects.includes(subject) ? alpha(BRAND.a, 0.08) : 'transparent',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: BRAND.a,
                      bgcolor: alpha(BRAND.a, 0.05),
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        bgcolor: teacherSubjects.includes(subject) ? BRAND.a : '#e2e8f0',
                        color: 'white',
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MenuBook sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ color: BRAND.ink }}>
                          {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>
                          {subject === 'Mathematics' ? 'Algebra, Geometry, Calculus' : 'Physics, Chemistry, Biology'}
                        </Typography>
                      </Box>
                    </Box>
                    <Checkbox
                      checked={teacherSubjects.includes(subject)}
                      sx={{
                        color: BRAND.a,
                        '&.Mui-checked': {
                          color: BRAND.a,
                        },
                      }}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
            {teacherSubjects.length === 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fef3c7', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600 }}>
                  ⚠️ Please select at least one subject
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenSubjectDialog(false)} sx={{ color: BRAND.muted }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSubjects}
              disabled={savingSubjects || teacherSubjects.length === 0}
              sx={{
                background: BRAND.gradient,
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: BRAND.gradient,
                  opacity: 0.9,
                },
                '&:disabled': {
                  bgcolor: '#e2e8f0',
                  color: '#94a3b8',
                }
              }}
            >
              {savingSubjects ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Subjects'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exam Management Dialog */}
        <Dialog open={openExamDialog} onClose={handleCloseExamDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="700">
                {selectedExam ? 'Edit Exam' : 'Create New Exam'}
              </Typography>
              <IconButton onClick={handleCloseExamDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Exam Name"
                  value={examFormData.name}
                  onChange={(e) => handleExamFormChange('name', e.target.value)}
                  placeholder="e.g., Mid-Term Mathematics Exam"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Exam Type"
                  value={examFormData.type}
                  onChange={(e) => handleExamFormChange('type', e.target.value)}
                  required
                >
                  {examTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Grade"
                  value={examFormData.grade}
                  onChange={(e) => handleExamFormChange('grade', e.target.value)}
                  required
                >
                  {grades.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Exam Date"
                  value={examFormData.exam_date}
                  onChange={(e) => handleExamFormChange('exam_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={examFormData.status}
                  onChange={(e) => handleExamFormChange('status', e.target.value)}
                >
                  {examStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description (Optional)"
                  value={examFormData.description}
                  onChange={(e) => handleExamFormChange('description', e.target.value)}
                  placeholder="Add exam details or instructions..."
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: BRAND.ink }}>
                    Exam Subjects
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddExamSubject}
                    sx={{ color: BRAND.a }}
                  >
                    Add Subject
                  </Button>
                </Box>
                {examSubjects.map((subject, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Subject"
                          value={subject.subject_name}
                          onChange={(e) => handleExamSubjectChange(index, 'subject_name', e.target.value)}
                        >
                          {availableSubjects.map((subj) => (
                            <MenuItem key={subj} value={subj}>
                              {subj}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={5} sm={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Total Marks"
                          value={subject.total_marks}
                          onChange={(e) => handleExamSubjectChange(index, 'total_marks', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={5} sm={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Pass Marks"
                          value={subject.passing_marks}
                          onChange={(e) => handleExamSubjectChange(index, 'passing_marks', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveExamSubject(index)}
                          disabled={examSubjects.length === 1}
                          sx={{
                            color: '#ef4444',
                            '&:disabled': {
                              color: '#cbd5e1'
                            }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseExamDialog} sx={{ color: BRAND.muted }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveExam}
              disabled={savingExam}
              sx={{
                background: BRAND.gradient,
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: BRAND.gradient,
                  opacity: 0.9,
                },
                '&:disabled': {
                  bgcolor: '#e2e8f0',
                  color: '#94a3b8',
                }
              }}
            >
              {savingExam ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (selectedExam ? 'Update Exam' : 'Create Exam')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Student Dashboard
  const StudentDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
    const [studentSubjects, setStudentSubjects] = useState([]);
    const [openEditSubjects, setOpenEditSubjects] = useState(false);
    const [tempSubjects, setTempSubjects] = useState([]);
    const [savingSubjects, setSavingSubjects] = useState(false);

    const BRAND = {
      a: '#5c55f3',
      b: '#7c75ff',
      gradient: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
      gradientAlt: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
      surface: 'rgba(255, 255, 255, 0.95)',
      ink: '#0f172a',
      muted: '#64748b',
    };

    useEffect(() => {
      fetchStudentStats();
    }, []);

    useEffect(() => {
      // Always show subject selection dialog when student has subjects and hasn't selected yet
      if (studentSubjects.length > 0 && !selectedSubject) {
        setOpenSubjectDialog(true);
      }
    }, [studentSubjects, selectedSubject]);

    const fetchStudentStats = async (subject = null) => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = {};
        if (subject) {
          params.subject = subject;
        }
        
        const response = await axios.get(`${API_URL}/dashboard/student`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setDashboardData(response.data);
        
        // Process exam marks data for chart - check multiple possible field names
        const examResults = response.data.exam_results || response.data.recent_results || [];
        if (examResults && examResults.length > 0) {
          const chartData = examResults
            .sort((a, b) => {
              const dateA = new Date(a.exam_date || a.exam?.exam_date);
              const dateB = new Date(b.exam_date || b.exam?.exam_date);
              return dateA - dateB;
            })
            .map(result => {
              const examDate = result.exam_date || result.exam?.exam_date;
              const totalMarks = parseFloat(result.total_marks || result.exam?.total_marks || 100);
              // Backend sends marks_obtained, not marks
              const marks = parseFloat(result.marks_obtained || result.marks) || 0;
              
              return {
                examName: result.exam_name || result.exam?.name || result.exam || 'Exam',
                date: examDate ? new Date(examDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
                marks: marks,
                total: totalMarks,
                percentage: marks && totalMarks ? ((marks / totalMarks) * 100).toFixed(1) : 0,
                subject: result.subject || subject || 'All',
              };
            });
          
          response.data.chartData = chartData;
          setDashboardData({...response.data, chartData});
        } else {
          setDashboardData({...response.data, chartData: []});
        }
        
        // Extract student's subjects from response (only on first load)
        if (response.data.student_info?.subjects && studentSubjects.length === 0) {
          const subjects = typeof response.data.student_info.subjects === 'string'
            ? JSON.parse(response.data.student_info.subjects)
            : response.data.student_info.subjects;
          setStudentSubjects(Array.isArray(subjects) ? subjects : []);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    const handleSubjectSelect = (subject) => {
      setSelectedSubject(subject);
      setOpenSubjectDialog(false);
      // Fetch data filtered by selected subject
      fetchStudentStats(subject);
    };

    const handleChangeSubject = () => {
      setSelectedSubject('');
      setOpenSubjectDialog(true);
    };

    const getGradeColor = (grade) => {
      const colors = {
        'A': '#16a34a',   // Over 75 - Green
        'B': '#3b82f6',   // 65-74 - Blue
        'C': '#f59e0b',   // 50-64 - Orange
        'S': '#f97316',   // 35-49 - Dark Orange
        'F': '#ef4444',   // Below 35 - Red
      };
      return colors[grade] || '#64748b';
    };

    const overallGrade = dashboardData?.overall_grade || 'N/A';
    const attendance = dashboardData?.attendance_percentage || 0;

    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: BRAND.gradient,
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
      }}>
        {/* Modern Header */}
        <Box sx={{ 
          mb: 4, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 }, 
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.18)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar sx={{ 
              width: 80, 
              height: 80, 
              background: BRAND.gradient,
              fontSize: 36,
              fontWeight: 'bold',
              boxShadow: '0 8px 24px rgba(92, 85, 243, 0.5)'
            }}>
              {user?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" fontWeight="800" sx={{ 
                background: BRAND.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}>
                {dashboardData?.student_info?.full_name || user?.name}
              </Typography>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                {dashboardData?.student_info?.student_index || 'Student Portal'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {selectedSubject && (
                  <>
                    <Chip 
                      label={`📚 ${selectedSubject}`}
                      size="medium"
                      sx={{ 
                        bgcolor: alpha('#10b981', 0.15),
                        color: '#10b981',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        py: 2
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleChangeSubject}
                      sx={{
                        borderColor: alpha(BRAND.a, 0.3),
                        color: BRAND.a,
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: BRAND.a,
                          bgcolor: alpha(BRAND.a, 0.08),
                        }
                      }}
                    >
                      Change Subject
                    </Button>
                  </>
                )}
                {dashboardData?.student_info?.grade && (
                  <Chip 
                    label={`Grade ${dashboardData.student_info.grade}`}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(BRAND.a, 0.15),
                      color: BRAND.a,
                      fontWeight: 600
                    }}
                  />
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<School />}
                  onClick={() => {
                    setTempSubjects([...studentSubjects]);
                    setOpenEditSubjects(true);
                  }}
                  sx={{
                    background: BRAND.gradient,
                    color: '#fff',
                    fontSize: '0.75rem',
                    '&:hover': {
                      background: BRAND.gradientAlt,
                    }
                  }}
                >
                  Manage My Subjects
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {loading && (
          <Paper sx={{
            mb: 4,
            p: 4,
            background: BRAND.surface,
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={22} sx={{ color: BRAND.a }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND.ink }}>
                  Loading {selectedSubject ? `${selectedSubject} data` : 'your dashboard'}…
                </Typography>
                <LinearProgress
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha('#ffffff', 0.45),
                    '& .MuiLinearProgress-bar': { background: BRAND.gradient },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}

       { /* Subject Filter Info Banner */}
          {selectedSubject && !loading && (
            <Paper sx={{
              mb: 3,
              p: 2.5,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: `2px solid ${BRAND.a}`,
              boxShadow: `0 4px 20px ${alpha(BRAND.a, 0.2)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              bgcolor: BRAND.a,
              color: 'white',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MenuBook sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="700" sx={{ color: BRAND.ink }}>
                Viewing: {selectedSubject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.muted }}>
                All data below is filtered by {selectedSubject}
              </Typography>
            </Box>
                </Box>
                <Chip 
            label="Active Filter"
            size="small"
            sx={{ 
              bgcolor: BRAND.a,
              color: 'white',
              fontWeight: 700,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 },
              }
            }}
                />
              </Box>
            </Paper>
          )}

          {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: BRAND.gradient, 
              color: 'white', 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(92, 85, 243, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 48px rgba(92, 85, 243, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <EmojiEvents sx={{ fontSize: 40 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Overall Performance
                  </Typography>
                </Box>
                <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 3 }}>
                  {overallGrade}
                </Typography>
                {dashboardData?.overall_percentage !== undefined && (
                  <>
                    <Typography variant="h5" align="center" sx={{ opacity: 0.95, mb: 1 }}>
                      {dashboardData.overall_percentage}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(dashboardData.overall_percentage, 100)} 
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.3)', 
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': { bgcolor: 'white' } 
                      }} 
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              color: 'white', 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 48px rgba(240, 147, 251, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Assessment sx={{ fontSize: 40 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Last Paper Marks
                  </Typography>
                </Box>
                {dashboardData?.recent_results && dashboardData.recent_results.length > 0 && !dashboardData.recent_results[0].is_absent ? (
                  <>
                    <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 3 }}>
                      {dashboardData.recent_results[0].marks_obtained}/{dashboardData.recent_results[0].total_marks}
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ opacity: 0.95, mb: 1 }}>
                      {dashboardData.recent_results[0].percentage}%
                    </Typography>
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Chip 
                        label={dashboardData.recent_results[0].grade}
                        size="small"
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.3)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.875rem'
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', my: 3 }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                      {dashboardData?.recent_results?.[0]?.is_absent ? 'Absent' : 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      No recent exam data
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 
              color: 'white', 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(17, 153, 142, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 48px rgba(17, 153, 142, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <TrendingUp sx={{ fontSize: 40 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Attendance
                  </Typography>
                </Box>
                <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 3 }}>
                  {attendance}%
                </Typography>
                <Typography variant="subtitle1" align="center" sx={{ opacity: 0.95, mb: 1 }}>
                  {dashboardData?.exams_attended || 0} / {dashboardData?.total_exams || 0} Exams
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={attendance} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.3)', 
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': { bgcolor: 'white' } 
                  }} 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
              color: 'white', 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(250, 112, 154, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 48px rgba(250, 112, 154, 0.5)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Assignment sx={{ fontSize: 40 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Exams This Month
                  </Typography>
                </Box>
                <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 3 }}>
                  {dashboardData?.exams_this_month || 0}
                </Typography>
                <Typography variant="subtitle1" align="center" sx={{ opacity: 0.95 }}>
                  {dashboardData?.upcoming_exams?.length || 0} upcoming exams
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Stats Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: '#16a34a', mb: 0.5 }}>
                {dashboardData?.exams_passed || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Exams Passed
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: '#ef4444', mb: 0.5 }}>
                {dashboardData?.exams_failed || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Exams Failed
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: '#3b82f6', mb: 0.5 }}>
                {dashboardData?.total_subjects || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Subjects
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: '#9333ea', mb: 0.5 }}>
                {dashboardData?.average_marks || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Average Marks
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Exam Marks Line Chart */}
        <Paper sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: { xs: 3, md: 4 },
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: { xs: 2, md: 3 },
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: '1px solid rgba(255,255,255,0.18)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 4,
                height: { xs: 24, md: 32 },
                background: BRAND.gradient,
                borderRadius: 2,
                mr: 2
              }} />
              <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink, fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                📈 Exam Performance Trend
              </Typography>
            </Box>
            {selectedSubject && (
              <Chip 
                label={`${selectedSubject} Results`}
                size="small"
                sx={{ 
                  bgcolor: alpha(BRAND.a, 0.15),
                  color: BRAND.a,
                  fontWeight: 600
                }}
              />
            )}
          </Box>
          
          {dashboardData?.chartData && dashboardData.chartData.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Chip 
                  icon={<TrendingUp />}
                  label={`${dashboardData.chartData.length} Exams`}
                  size="small"
                  sx={{ 
                    bgcolor: alpha('#10b981', 0.1),
                    color: '#10b981',
                    fontWeight: 600
                  }}
                />
                <Chip 
                  icon={<Assessment />}
                  label={`Highest: ${Math.max(...dashboardData.chartData.map(d => parseFloat(d.marks)))} marks`}
                  size="small"
                  sx={{ 
                    bgcolor: alpha(BRAND.a, 0.1),
                    color: BRAND.a,
                    fontWeight: 600
                  }}
                />
              </Box>
              
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={dashboardData.chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b"
                    style={{ fontSize: '0.875rem', fontWeight: 600 }}
                  />
                  <YAxis 
                    stroke="#64748b"
                    style={{ fontSize: '0.875rem', fontWeight: 600 }}
                    domain={[0, 100]}
                    label={{ value: 'Marks', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontWeight: 600 } }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid #5c55f3',
                      borderRadius: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      padding: '12px 16px'
                    }}
                    labelStyle={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}
                    itemStyle={{ fontWeight: 600 }}
                    formatter={(value, name, props) => {
                      if (name === 'marks') {
                        return [`${value} marks (out of ${props.payload.total})`, props.payload.examName];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: 20, fontWeight: 600 }}
                    iconType="line"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="marks" 
                    stroke="#5c55f3" 
                    strokeWidth={3}
                    dot={{ fill: '#5c55f3', r: 6 }}
                    activeDot={{ r: 8, fill: '#7c75ff', stroke: '#5c55f3', strokeWidth: 2 }}
                    name="Exam Marks"
                  />
                </LineChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 3, p: 2, bgcolor: alpha(BRAND.a, 0.05), borderRadius: 2, border: `1px solid ${alpha(BRAND.a, 0.2)}` }}>
                <Typography variant="caption" sx={{ color: BRAND.muted, display: 'block', textAlign: 'center' }}>
                  💡 Tip: Hover over data points to see exam name and marks scored. {selectedSubject ? `Showing ${selectedSubject} exam marks over time.` : 'Select a subject to view subject-specific trends.'}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Assessment sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500, mb: 1 }}>
                No exam data available
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                {selectedSubject ? `No ${selectedSubject} exam results found yet` : 'Your exam marks will appear here once results are published'}
              </Typography>
            </Box>
          )}
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Recent Exam Results
                </Typography>
              </Box>
              
              {dashboardData?.recent_results && dashboardData.recent_results.length > 0 ? (
                <Box>
                  {dashboardData.recent_results.map((result, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2.5,
                        mb: 2,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="600" sx={{ color: BRAND.ink }}>
                            {result.exam}
                          </Typography>
                          <Typography variant="body2" sx={{ color: BRAND.muted }}>
                            {result.subject} • {result.exam_date}
                          </Typography>
                        </Box>
                        <Chip 
                          label={result.is_absent ? 'Absent' : result.grade}
                          size="small"
                          sx={{ 
                            bgcolor: result.is_absent ? '#fee2e2' : alpha(getGradeColor(result.grade), 0.15),
                            color: result.is_absent ? '#dc2626' : getGradeColor(result.grade),
                            fontWeight: 700,
                            minWidth: 60
                          }}
                        />
                      </Box>
                      {!result.is_absent && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={result.percentage} 
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                bgcolor: '#e2e8f0',
                                '& .MuiLinearProgress-bar': { 
                                  bgcolor: getGradeColor(result.grade),
                                  borderRadius: 4
                                } 
                              }} 
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="700" sx={{ color: BRAND.ink, minWidth: 80 }}>
                            {result.marks_obtained}/{result.total_marks} ({result.percentage}%)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Assessment sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    No results available yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    Your exam results will appear here once published
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              mb: 3,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Quick Actions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  sx={{ 
                    background: BRAND.gradient,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 8px 20px rgba(92, 85, 243, 0.35)',
                    '&:hover': {
                      boxShadow: '0 12px 28px rgba(92, 85, 243, 0.45)',
                    }
                  }}
                  startIcon={<Assessment />}
                  onClick={() => navigate('/student/results')}
                >
                  View All Results
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  sx={{ 
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderColor: BRAND.a,
                    color: BRAND.a,
                    '&:hover': {
                      borderColor: BRAND.b,
                      bgcolor: alpha(BRAND.a, 0.05),
                    }
                  }}
                  startIcon={<Person />}
                  onClick={() => navigate('/student/profile')}
                >
                  My Profile
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  sx={{ 
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderColor: BRAND.a,
                    color: BRAND.a,
                    '&:hover': {
                      borderColor: BRAND.b,
                      bgcolor: alpha(BRAND.a, 0.05),
                    }
                  }}
                  startIcon={<CalendarToday />}
                  onClick={() => navigate('/student/exams')}
                >
                  Exam Schedule
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  sx={{ 
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderColor: '#10b981',
                    color: '#10b981',
                    '&:hover': {
                      borderColor: '#059669',
                      bgcolor: alpha('#10b981', 0.05),
                    }
                  }}
                  startIcon={<Assessment />}
                  onClick={() => navigate('/student/fee-payments')}
                >
                  My Fee Payments
                </Button>
              </Box>
            </Paper>

            {/* Grade Distribution Pie Chart */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              mb: 3,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Grade Distribution
                </Typography>
              </Box>
              
              {dashboardData?.grade_distribution && dashboardData.grade_distribution.some(g => g.value > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={dashboardData.grade_distribution.filter(g => g.value > 0 && g.name)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value}`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {dashboardData.grade_distribution.filter(g => g.value > 0 && g.name).map((entry, index) => {
                          const colors = {
                            'A': '#16a34a',   // Green
                            'B': '#3b82f6',   // Blue
                            'C': '#f59e0b',   // Orange
                            'S': '#f97316',   // Dark Orange
                            'F': '#ef4444',   // Red
                          };
                          return <Cell key={`cell-${index}`} fill={colors[entry.name] || '#64748b'} />;
                        })}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(255, 255, 255, 0.98)',
                          border: '2px solid #5c55f3',
                          borderRadius: 8,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: '12px 16px'
                        }}
                        formatter={(value, name, props) => [`${value} exams`, `Grade ${props.payload.name}`]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                      {dashboardData.grade_distribution.filter(g => g.value > 0 && g.name).map((grade, index) => {
                        const colors = {
                          'A': '#16a34a',
                          'B': '#3b82f6',
                          'C': '#f59e0b',
                          'S': '#f97316',
                          'F': '#ef4444',
                        };
                        return (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              bgcolor: colors[grade.name] || '#64748b'
                            }} />
                            <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                              {grade.name} ({grade.value})
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2, p: 2, bgcolor: alpha(BRAND.a, 0.05), borderRadius: 2, border: `1px solid ${alpha(BRAND.a, 0.2)}` }}>
                    <Typography variant="caption" sx={{ color: BRAND.muted, display: 'block', textAlign: 'center' }}>
                      📊 Distribution of your grades across all {selectedSubject ? selectedSubject : ''} exam results
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Assessment sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      No grade data available
                    </Typography>
                    {dashboardData?.grade_distribution && (
                      <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 1, display: 'block' }}>
                        Debug: {dashboardData.grade_distribution.length} grades found but all have 0 values
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Paper>

            {/* Upcoming Exams */}
            <Paper sx={{ 
              p: { xs: 3, md: 4 },
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Upcoming Exams
                </Typography>
              </Box>
              {dashboardData?.upcoming_exams && dashboardData.upcoming_exams.length > 0 ? (
                <Box>
                  {dashboardData.upcoming_exams.slice(0, 3).map((exam, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: BRAND.ink, mb: 0.5 }}>
                        {exam.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: BRAND.muted }}>
                        {exam.grade} • {exam.exam_date}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarToday sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    No upcoming exams
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Subject Selection Dialog */}
        <Dialog 
          open={openSubjectDialog} 
          onClose={() => {}} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="700" sx={{ 
                background: BRAND.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Select Your Subject
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                Choose a subject to view your dashboard
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {studentSubjects.map((subject) => (
                <Paper
                  key={subject}
                  onClick={() => handleSubjectSelect(subject)}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    border: '2px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: subject === 'Mathematics' ? BRAND.a : '#10b981',
                      bgcolor: subject === 'Mathematics' ? alpha(BRAND.a, 0.05) : alpha('#10b981', 0.05),
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: subject === 'Mathematics' ? alpha(BRAND.a, 0.15) : alpha('#10b981', 0.15),
                      color: subject === 'Mathematics' ? BRAND.a : '#10b981',
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MenuBook sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="700" sx={{ 
                        color: BRAND.ink,
                        mb: 0.5
                      }}>
                        {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND.muted }}>
                        {subject === 'Mathematics' 
                          ? 'View your math exams, results and progress' 
                          : 'View your science exams, results and progress'}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      color: subject === 'Mathematics' ? '#667eea' : '#10b981',
                      fontSize: 32
                    }}>
                      →
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
            {studentSubjects.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <MenuBook sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  No subjects assigned
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                  Please contact your administrator to assign subjects
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Subjects Dialog */}
        <Dialog 
          open={openEditSubjects} 
          onClose={() => setOpenEditSubjects(false)}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ 
            background: BRAND.gradient,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <School />
            Manage My Subjects
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
              Select the subjects you are studying. You can add or remove subjects anytime.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['Mathematics', 'Science'].map((subject) => (
                <Paper
                  key={subject}
                  elevation={0}
                  onClick={() => {
                    if (tempSubjects.includes(subject)) {
                      setTempSubjects(tempSubjects.filter(s => s !== subject));
                    } else {
                      setTempSubjects([...tempSubjects, subject]);
                    }
                  }}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    border: `2px solid ${tempSubjects.includes(subject) ? BRAND.a : '#e2e8f0'}`,
                    bgcolor: tempSubjects.includes(subject) ? alpha(BRAND.a, 0.08) : '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: BRAND.a,
                      bgcolor: alpha(BRAND.a, 0.05),
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(92, 85, 243, 0.2)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      background: tempSubjects.includes(subject) ? BRAND.gradient : '#f1f5f9',
                      color: tempSubjects.includes(subject) ? '#fff' : BRAND.muted,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MenuBook />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="700" sx={{ 
                        color: BRAND.ink,
                        mb: 0.5
                      }}>
                        {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND.muted }}>
                        {tempSubjects.includes(subject) ? 'Currently selected' : 'Click to select'}
                      </Typography>
                    </Box>
                    {tempSubjects.includes(subject) && (
                      <Box sx={{ color: '#10b981', fontSize: 28 }}>
                        ✓
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setOpenEditSubjects(false)}
              sx={{ color: BRAND.muted }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              disabled={savingSubjects || tempSubjects.length === 0}
              onClick={async () => {
                try {
                  setSavingSubjects(true);
                  const token = localStorage.getItem('token');
                  await axios.put(
                    `${API_URL}/student/subjects`,
                    { subjects: tempSubjects },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setStudentSubjects(tempSubjects);
                  setOpenEditSubjects(false);
                  alert('Subjects updated successfully!');
                  // Refresh dashboard data
                  fetchStudentStats(selectedSubject);
                } catch (error) {
                  alert('Failed to update subjects. Please try again.');
                } finally {
                  setSavingSubjects(false);
                }
              }}
              sx={{
                background: BRAND.gradient,
                color: '#fff',
                '&:hover': {
                  background: BRAND.gradientAlt,
                }
              }}
            >
              {savingSubjects ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  useEffect(() => {
    if (user?.role === 'fund_collector') {
      navigate('/fund-collector/dashboard');
    }
  }, [user, navigate]);

  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  if (user?.role === 'fund_collector') {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {user?.role === 'teacher' && <TeacherDashboard />}
      {user?.role === 'student' && <StudentDashboard />}
    </Container>
  );
};

export default Dashboard;
